import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import {
  Button,
  Stack,
  Typography,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ChessButton from "./ChessButton";
import { useTranslation } from "react-i18next";
import i18n from "../services/i18n";
import { useNavigate } from "react-router-dom";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SettingsIcon from "@mui/icons-material/Settings";

import EnhancedChessDrawer from "./EnhancedChessDrawer";

const ResponsiveChessPageBlind = ({ setShowAppBar }) => {
  const game = useMemo(() => new Chess(), []);
  const [fen, setFen] = useState(game.fen());
  const [fromSquare, setFromSquare] = useState(null);
  const [toSquare, setToSquare] = useState(null);
  const [, setHighlightedSquares] = useState({});
  const [playerColor, setPlayerColor] = useState("w");
  const [hiddenMode, setHiddenMode] = useState(false);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [aiLevel, setAiLevel] = useState(5);
  const [lastMove, setLastMove] = useState(null);
  const [invalidCount, setInvalidCount] = useState({ w: 0, b: 0 });
  const [autoOpponentMove, setAutoOpponentMove] = useState(false);
  const [positionDrawerOpen, setPositionDrawerOpen] = useState(false);
  const [capturedWhite, setCapturedWhite] = useState([]);
  const [capturedBlack, setCapturedBlack] = useState([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(false);
  const [showSpeechPrompt, setShowSpeechPrompt] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const stockfish = useRef(null);

  // Custom hook for responsive design values
  const useResponsiveValues = () => {
    const isMobile = useMediaQuery("(max-width: 600px)");
    return {
      boardWidth: isMobile ? 350 : 700,
      fontSize: isMobile ? "9px" : "14px",
      buttonWidth: isMobile ? "120px" : "220px",
      buttonHeight: isMobile ? "60px" : "70px",
      capturedPiecesTop: isMobile ? -18 : 1,
      borderWidth: isMobile ? "1px" : "5px",
      marginTop: isMobile ? "45px" : "30px",
      isMobile,
    };
  };

  const responsive = useResponsiveValues();
  const isMobile = useMediaQuery("(max-width:600px)");
  const boardWidth = isMobile ? 330 : 500;
  const buttonStyle = {
    minWidth: isMobile ? 70 : 120,
    height: isMobile ? 36 : 44,
    fontSize: isMobile ? 12 : 16,
    borderRadius: 8,
    margin: isMobile ? "4px" : "6px",
  };
  const stackSpacing = isMobile ? 1 : 2;

  // Check if device is mobile
  const isMobileDevice = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Blind mode configuration
  const blindModeConfig = {
    // Primary Actions
    switchColor: true,
    aiSettings: true,
    
    // Game Actions (minimal for focus)
    offerDraw: false,
    saveGame: false,
    inviteViewer: false,
    shareScreen: false,
    showHistory: false,
    sendSmile: false,
    
    // Analysis
    gameAnalysis: false,
    
    // Position Controls
    setPosition: false,
    copyPgn: true,
    copyFen: true,
    
    // System
    gameInfo: false,
    settings: true,
    account: false,
    
    // Actions
    restartGame: true,
    surrender: false,
    mainMenu: true,
    
    // Blind Chess Features (all enabled)
    bestMove: true,
    speechSynthesis: true,
    autoAiMove: true,
    hiddenMode: true,
    sayLastMove: true,
    
    // Sections
    showPrimarySection: true,
    showGameActionsSection: false,
    showAnalysisSection: false,
    showPositionSection: true,
    showSystemSection: true,
    showActionsSection: true,
    showBlindSection: true, // Main focus
  };

  // Initialize speech permission check
  useEffect(() => {
    if (isMobileDevice) {
      const speechPermission = localStorage.getItem('speechEnabled');
      if (speechPermission === 'true') {
        setSpeechEnabled(true);
      } else if (speechPermission === null) {
        setShowSpeechPrompt(true);
      }
    } else {
      setSpeechEnabled(true);
    }
  }, [isMobileDevice]);

  // Function to test and enable speech
  const enableSpeech = useCallback(() => {
    if (!('speechSynthesis' in window)) {
      alert(t('Speech synthesis not supported'));
      return;
    }

    const testUtterance = new SpeechSynthesisUtterance(t('Speech enabled'));
    testUtterance.volume = 0.1;
    testUtterance.rate = 2;
    
    testUtterance.onstart = () => {
      console.log('Speech test successful');
      setSpeechEnabled(true);
      localStorage.setItem('speechEnabled', 'true');
      setShowSpeechPrompt(false);
    };

    testUtterance.onerror = (event) => {
      console.error('Speech test failed:', event);
      alert(t('Speech synthesis failed. Please check your device settings.'));
    };

    window.speechSynthesis.speak(testUtterance);
  }, [t]);

  // Function to disable speech
  const disableSpeech = useCallback(() => {
    setSpeechEnabled(false);
    localStorage.setItem('speechEnabled', 'false');
    setShowSpeechPrompt(false);
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  }, []);

  // Enhanced speech function that respects permission
  const speakText = useCallback((text, lang = i18n.language, updateButtonState = true) => {
    if (!speechEnabled) {
      console.log('Speech disabled');
      return;
    }

    window.speechSynthesis.cancel();
    
    setTimeout(() => {
      if (updateButtonState) {
        setIsSpeaking(true);
      }
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      utterance.rate = isMobileDevice ? 0.7 : 0.8;
      utterance.volume = 0.9;
      utterance.pitch = 1.0;

      if (isMobileDevice && updateButtonState) {
        const estimatedDuration = (text.length * 80) + 1000;
        const maxDuration = Math.min(estimatedDuration, 8000);
        
        setTimeout(() => {
          setIsSpeaking(false);
        }, maxDuration);
      } else {
        utterance.onend = () => {
          if (updateButtonState) {
            setIsSpeaking(false);
          }
        };

        utterance.onerror = () => {
          if (updateButtonState) {
            setIsSpeaking(false);
          }
        };
      }

      try {
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.error('Speech synthesis error:', error);
        if (updateButtonState) {
          setIsSpeaking(false);
        }
      }
    }, isMobileDevice ? 200 : 100);
  }, [speechEnabled, isMobileDevice]);

  const speakMove = useCallback((from, to, color, lang = i18n.language) => {
    const convert = (sq) => `${sq[0]} ${sq[1]}`;
    const colorWord = color === "w" ? t("White") : t("Black");
    const moveText = `${colorWord} ${convert(from)} ${convert(to)}`;
    speakText(moveText, lang, false); // Don't update button state for auto moves
  }, [speakText, t]);

  // Handle say last move button
  const handleSayLastMove = useCallback(() => {
    if (!speechEnabled) {
      setShowSpeechPrompt(true);
      return;
    }

    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    if (!lastMove) {
      speakText(t("No moves yet"));
      return;
    }

    const convert = (sq) => `${sq[0]} ${sq[1]}`;
    const colorWord = lastMove.color === "w" ? t("White") : t("Black");
    const moveText = `${colorWord} ${convert(lastMove.from)} ${convert(lastMove.to)}`;
    speakText(moveText, i18n.language, true);
  }, [speechEnabled, isSpeaking, lastMove, speakText, t]);

  // Fixed drawer handlers
  const handleOpenDrawer = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setPositionDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback((e, reason) => {
    if (responsive.isMobile && reason === 'backdropClick') {
      return;
    }
    setPositionDrawerOpen(false);
  }, [responsive.isMobile]);

  const togglePlayerColor = useCallback(() => {
    setPlayerColor((prev) => (prev === "w" ? "b" : "w"));
  }, []);

  useEffect(() => {
    const persistedLanguage = localStorage.getItem("selectedLanguage");
    if (persistedLanguage) {
      const lang = JSON.parse(persistedLanguage);
      i18n.changeLanguage(lang.code);
    }
  }, []);

  useEffect(() => {
    setShowAppBar(isMobile ? false : true);
    const isLocalhost = window.location.hostname === "localhost";
    stockfish.current = new Worker(
      isLocalhost
        ? `${process.env.PUBLIC_URL}/stockfish.wasm.js`
        : `${process.env.PUBLIC_URL}/stockfish.wasm.js`
    );
    stockfish.current.onmessage = (event) => {
      if (event.data.startsWith("bestmove")) {
        const [, best] = event.data.split(" ");
        if (typeof best === "string" && best !== "(none)" && best.length >= 4) {
          const from = best.substring(0, 2);
          const to = best.substring(2, 4);
          handleMove(from, to, true);
        }
      }
    };

    updateCapturedPieces();

    return () => {
      if (stockfish.current) {
        stockfish.current.terminate();
      }
    };
  }, [isMobile, setShowAppBar]);

  const updateCapturedPieces = useCallback(() => {
    const initialCounts = {
      w: { p: 8, r: 2, n: 2, b: 2, q: 1, k: 1 },
      b: { p: 8, r: 2, n: 2, b: 2, q: 1, k: 1 },
    };

    const currentCounts = {
      w: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 },
      b: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 },
    };

    game?.board().flat().forEach((square) => {
      if (square) {
        currentCounts[square.color][square.type]++;
      }
    });

    const newWhiteCaptured = [];
    const newBlackCaptured = [];
    const pieceImportance = { k: 1, q: 2, r: 3, b: 4, n: 5, p: 6 };

    Object.keys(initialCounts.w).forEach((piece) => {
      const missingWhitePieces = initialCounts.w[piece] - currentCounts.w[piece];
      const missingBlackPieces = initialCounts.b[piece] - currentCounts.b[piece];

      if (missingWhitePieces > 0)
        newBlackCaptured.push(...Array(missingWhitePieces).fill(piece));
      if (missingBlackPieces > 0)
        newWhiteCaptured.push(...Array(missingBlackPieces).fill(piece));
    });

    const sortedWhiteCaptured = newWhiteCaptured.sort(
      (a, b) => pieceImportance[a] - pieceImportance[b]
    );
    const sortedBlackCaptured = newBlackCaptured.sort(
      (a, b) => pieceImportance[a] - pieceImportance[b]
    );

    setCapturedWhite(sortedWhiteCaptured);
    setCapturedBlack(sortedBlackCaptured);
  }, [game]);

  const handleMove = useCallback((from, to, isAiMove = false) => {
    const piece = game.get(from);
    if (!piece) {
      speakText(t("noPieceAt", { square: from }));
      return false;
    }

    try {
      const move = game.move({ from, to, promotion: "q" });
      if (move) {
        setFen(game.fen());
        setLastMove({ from, to, color: move.color });
        speakMove(from, to, move.color);
        updateCapturedPieces();

        if (autoOpponentMove && !isAiMove && !game.isGameOver()) {
          const currentTurn = game.turn();
          const isOpponentTurn = currentTurn !== playerColor;

          if (isOpponentTurn) {
            setTimeout(() => {
              if (!game.isGameOver()) {
                runAiMove();
              }
            }, 1500);
          }
        }

        return true;
      } else {
        incrementInvalidCount(piece.color);
        speakText(t("invalidMove", { from, to }));
        return false;
      }
    } catch {
      if (piece?.color) incrementInvalidCount(piece.color);
      speakText(t("invalidMove", { from, to }));
      return false;
    }
  }, [game, speakMove, speakText, updateCapturedPieces, autoOpponentMove, playerColor, t]);

  const incrementInvalidCount = (color) => {
    setInvalidCount((prev) => ({ ...prev, [color]: prev[color] + 1 }));
  };

  const handleResetGame = useCallback(() => {
    game.reset();
    setFen(game.fen());
    setFromSquare(null);
    setToSquare(null);
    setHighlightedSquares({});
    setSelectedSquare(null);
    setLegalMoves([]);
    setInvalidCount({ w: 0, b: 0 });
    setLastMove(null);
    setCapturedWhite([]);
    setCapturedBlack([]);
  }, [game]);

  const handleSquareClick = (square) => {
    if (hiddenMode) {
      if (!fromSquare) {
        setFromSquare(square);
        setHighlightedSquares({
          [square]: { backgroundColor: "rgba(255,255,0,0.4)" },
        });
      } else if (!toSquare && square !== fromSquare) {
        setToSquare(square);
        setHighlightedSquares((prev) => ({
          ...prev,
          [square]: { backgroundColor: "rgba(0,255,0,0.4)" },
        }));
      }
      return;
    }

    if (selectedSquare === square) {
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }

    if (!selectedSquare) {
      const piece = game.get(square);
      if (piece && piece.color === game.turn()) {
        const moves = game.moves({ square, verbose: true });
        if (moves.length === 0) return;

        const legalDestinations = moves.map((m) => m.to);
        setSelectedSquare(square);
        setLegalMoves(legalDestinations);
        return;
      }
    }

    if (selectedSquare) {
      const moveResult = handleMove(selectedSquare, square, false);
      if (moveResult) {
        setSelectedSquare(null);
        setLegalMoves([]);
      } else {
        const piece = game.get(square);
        if (piece && piece.color === game.turn()) {
          const moves = game.moves({ square, verbose: true });
          if (moves.length > 0) {
            const legalDestinations = moves.map((m) => m.to);
            setSelectedSquare(square);
            setLegalMoves(legalDestinations);
          } else {
            setSelectedSquare(null);
            setLegalMoves([]);
          }
        } else {
          setSelectedSquare(null);
          setLegalMoves([]);
        }
      }
    }
  };

  const approveMove = () => {
    handleMove(fromSquare, toSquare, false);
    setFromSquare(null);
    setToSquare(null);
    setHighlightedSquares({});
  };

  const clearMove = () => {
    setFromSquare(null);
    setToSquare(null);
    setHighlightedSquares({});
    setSelectedSquare(null);
    setLegalMoves([]);
  };

  const runAiMove = useCallback(() => {
    if (!stockfish.current) return;

    const currentTurn = game.turn();
    const isOpponentTurn = currentTurn !== playerColor;
    if (!isOpponentTurn) {
      return;
    }

    stockfish.current.postMessage("uci");
    stockfish.current.postMessage(`setoption name Skill Level value ${aiLevel}`);
    stockfish.current.postMessage("ucinewgame");
    stockfish.current.postMessage(`position fen ${game.fen()}`);
    stockfish.current.postMessage(`go depth 15`);
  }, [game, playerColor, aiLevel]);

  const getLastMoveText = () => {
    if (!lastMove) return t("sayLastMove");
    return `${lastMove.from}-${lastMove.to}`;
  };

  const combinedSquareStyles = useMemo(() => {
    const styles = {};

    if (hiddenMode) {
      if (fromSquare) {
        styles[fromSquare] = {
          backgroundColor: "rgba(255,255,0,0.4)",
        };
      }
      if (toSquare) {
        styles[toSquare] = {
          backgroundColor: "rgba(0,255,0,0.4)",
        };
      }
      return styles;
    }

    if (selectedSquare) {
      styles[selectedSquare] = {
        backgroundColor: "rgba(255, 255, 0, 0.6)",
        borderRadius: "50%",
      };
    }

    legalMoves.forEach((sq) => {
      const piece = game.get(sq);
      const isCapture = piece && piece.color !== game.turn();

      styles[sq] = {
        backgroundColor: isCapture
          ? "rgba(255, 0, 0, 0.4)"
          : "rgba(0, 255, 0, 0.3)",
        borderRadius: "50%",
      };
    });

    return styles;
  }, [selectedSquare, legalMoves, game, hiddenMode, fromSquare, toSquare]);

  const CapturedPieces = ({ pieces, color, position, isMobile }) => {
    const hasPieces = pieces && pieces.length > 0;

    return (
      <div
        className="captured-pieces-container"
        style={{
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "5px",
          minHeight: "50px",
        }}
      >
        {hasPieces && (
          <div
            style={{
              display: "flex",
              gap: "3px",
              padding: "6px 10px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "6px",
              top: position === "top" ? (isMobile ? -20 : 1) : 0,
              position: "relative",
              zIndex: 10,
            }}
          >
            {pieces.map((piece, index) =>
              piece ? (
                <img
                  key={index}
                  src={`https://res.cloudinary.com/dnlgtek2a/image/upload/v1743416894/Figures/${piece}_${color}.svg`}
                  alt={`${color} ${piece}`}
                  style={{ width: "20px", height: "20px" }}
                />
              ) : null
            )}
          </div>
        )}
      </div>
    );
  };

  // Create a mock gameState for the drawer
  const gameState = {
    autoAIMoves: autoOpponentMove,
  };

  // Mock dispatch function
  const dispatch = (action) => {
    if (action.type === "SET_AI_LEVEL") {
      setAiLevel(action.payload);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: isMobile ? 1 : 18,
      }}
    >
      {/* <div
        style={{
          marginTop: isMobile ? "-50px" : "-50px",
          width: "100%",
          alignItems: "center",
          marginBottom: "20px",
          justifyContent: "center",
          gap: "10px",
          display: "flex",
        }}
      >
        <ChessButton
          onPress={() => setHiddenMode((prev) => !prev)}
          fontSize={responsive.fontSize}
          width={responsive.buttonWidth}
          height={responsive.buttonHeight}
          text={hiddenMode ? t("showBoard") : t("hideBoard")}
          icon={
            hiddenMode ? (
              <VisibilityIcon
                sx={{
                  fontSize: isMobile ? 16 : 20,
                  marginRight: "5px",
                }}
              />
            ) : (
              <VisibilityOffIcon
                sx={{
                  fontSize: isMobile ? 16 : 20,
                  marginRight: "5px",
                }}
              />
            )
          }
        />
      </div> */}
      
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <div style={{ maxWidth: boardWidth }}>
          {!hiddenMode && (
            <div>
              <CapturedPieces
                pieces={playerColor === "b" ? capturedWhite : capturedBlack}
                color={playerColor === "b" ? "b" : "w"}
                position="top"
                isMobile={isMobile}
              />
            </div>
          )}

          <Chessboard
            position={hiddenMode ? "" : fen}
            onPieceDrop={
              !hiddenMode
                ? (src, tgt) => handleMove(src, tgt, false)
                : undefined
            }
            onSquareClick={handleSquareClick}
            arePiecesDraggable={!hiddenMode}
            customSquareStyles={combinedSquareStyles}
            boardOrientation={playerColor === "w" ? "white" : "black"}
            boardWidth={boardWidth}
          />

          {!hiddenMode && (
            <div>
              <CapturedPieces
                pieces={playerColor === "b" ? capturedBlack : capturedWhite}
                color={playerColor === "b" ? "w" : "b"}
                position="bottom"
                isMobile={isMobile}
              />
            </div>
          )}

          {!hiddenMode && selectedSquare && (
            <Typography
              variant="body2"
              style={{
                marginTop: "10px",
                color: "#4caf50",
                fontSize: isMobile ? 12 : 16,
                textAlign: "center",
              }}
            >
              {t("Selected")}: {selectedSquare} | {t("Legal moves")}:{" "}
              {legalMoves.length}
            </Typography>
          )}

          {hiddenMode && (fromSquare || toSquare) && (
            <Typography
              variant="body2"
              style={{
                marginTop: "10px",
                color: "#4caf50",
                fontSize: isMobile ? 12 : 16,
                textAlign: "center",
              }}
            >
              {fromSquare && !toSquare && `${t("From")}: ${fromSquare}`}
              {fromSquare &&
                toSquare &&
                `${t("Move")}: ${fromSquare} → ${toSquare}`}
            </Typography>
          )}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          border: `${responsive.borderWidth} solid rgb(96, 102, 104)`,
          borderRadius: "15px",
          padding: "4px",
          position: "relative",
          zIndex: 10,
          marginTop: "20px",
          gap: "5px",
          width: "fit-content",
          margin: "20px auto 0 auto",
        }}
      >
        <ChessButton
          text={getLastMoveText()}
          icon={
            <VolumeUpIcon
              sx={{
                fontSize: isMobile ? 16 : 20,
                marginRight: "5px",
              }}
            />
          }
          onPress={() => {
              setTimeout(() => handleSayLastMove(true), 100);
            }}
          
          fontSize={responsive.fontSize}
          width={responsive.buttonWidth}
          height={responsive.buttonHeight}
          fontColor={lastMove ? "white" : "#cccccc"}
          primaryColor={lastMove ? undefined : "#666666"}
          disabled={isSpeaking}
        />

        <ChessButton
          onPress={() => setHiddenMode((prev) => !prev)}
          fontSize={responsive.fontSize}
          width={responsive.buttonWidth}
          height={responsive.buttonHeight}
          text={hiddenMode ? t("showBoard") : t("hideBoard")}
          icon={
            hiddenMode ? (
              <VisibilityIcon
                sx={{
                  fontSize: isMobile ? 16 : 20,
                  marginRight: "5px",
                }}
              />
            ) : (
              <VisibilityOffIcon
                sx={{
                  fontSize: isMobile ? 16 : 20,
                  marginRight: "5px",
                }}
              />
            )
          }
        />

        <ChessButton
          text={t("Extra Controls")}
          onPress={handleOpenDrawer}
          fontSize={responsive.fontSize}
          width={responsive.buttonWidth}
          height={responsive.buttonHeight}
          icon={
            <SettingsIcon
              sx={{
                fontSize: isMobile ? 16 : 20,
                marginRight: "5px",
              }}
            />
          }
        />
      </div>

      {hiddenMode && fromSquare && toSquare && (
        <Stack direction="row" spacing={stackSpacing} marginTop={2}>
          <Button
            variant="outlined"
            style={{ ...buttonStyle, backgroundColor: "white" }}
            onClick={approveMove}
          >
            {t("approve")}
          </Button>
          <Button
            variant="outlined"
            style={{ ...buttonStyle, backgroundColor: "white" }}
            onClick={clearMove}
          >
            {t("clear")}
          </Button>
        </Stack>
      )}

      {/* Enhanced Drawer with Blind Mode Configuration */}
      <EnhancedChessDrawer
        open={positionDrawerOpen}
        onClose={handleCloseDrawer}
        responsive={responsive}
        t={t}
        gameState={gameState}
        dispatch={dispatch}
        game={game}
        togglePlayerColor={togglePlayerColor}
        sendToGameAnalysis={() => {}} // Not used in blind mode
        shouldShowAnalytics={() => false} // Not used in blind mode
        applyCustomFen={() => {}} // Not used in blind mode
        handleResetGame={handleResetGame}
        navigate={navigate}
        // ControlPanel props (minimal for blind mode)
        playerColorFromServer={playerColor}
        gameIdFromServer={null}
        onCaptureBoard={() => {}} // Not used in blind mode
        setIsViewer={false}
        gameOver={game.isGameOver()}
        onAccordionChange={() => {}}
        onGameHistoryChange={() => {}}
        getMoveHistory={() => []}
        aiLevel={aiLevel}
        gameInfo={null}
        winnerColor={null}
        learnMode={false}
        evaluation={null}
        showGameHistory={true}
        userData={null}
        topSubscription={null}
        generalMessageViaState={null}
        location={null}
        sendMessage={() => {}}
        generalMessageTemplate={{}}
        setShowHistoryLimitDialog={() => {}}
        //GENERAL={{}},
        // Button Configuration for Blind Mode
        buttonConfig={blindModeConfig}
        // Blind Chess Props
        speechEnabled={speechEnabled}
        setSpeechEnabled={setSpeechEnabled}
        enableSpeech={enableSpeech}
        disableSpeech={disableSpeech}
        autoOpponentMove={autoOpponentMove}
        setAutoOpponentMove={setAutoOpponentMove}
        runAiMove={runAiMove}
        speakText={speakText}
        hiddenMode={hiddenMode}
        setHiddenMode={setHiddenMode}
        lastMove={lastMove}
        handleSayLastMove={handleSayLastMove}
        invalidCount={invalidCount}
        isSpeaking={isSpeaking}

        drawerOptions={{
          allowDragDrop: true,
          initialPosition: 'right',
          keepBoardVisible: true,
          autoClose: false,
        }}
      />

      {/* Speech Permission Dialog */}
      <Dialog
        open={showSpeechPrompt}
        onClose={() => setShowSpeechPrompt(false)}
        sx={{
          zIndex: 10000,
        }}
      >
        <DialogTitle>
          <VolumeUpIcon sx={{ marginRight: 1, verticalAlign: 'middle' }} />
          {t("Enable Speech")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ marginBottom: 2 }}>
            {t("This chess app can announce moves and provide audio feedback. Would you like to enable speech synthesis?")}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            {t("Note: You can change this setting later in the Extra Controls menu.")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={disableSpeech} color="secondary">
            {t("No, Keep Silent")}
          </Button>
          <Button onClick={enableSpeech} variant="contained" color="primary">
            {t("Yes, Enable Speech")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ResponsiveChessPageBlind;